import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/DataTablePtBr';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

const compraInicial = {
  fornecedorId: '',
  safraId: '',
  dataCompra: new Date().toISOString().substring(0, 10),
  dataVencimento: new Date().toISOString().substring(0, 10),
  formaPagamento: 5,
  numeroNota: '',
  observacao: ''
};

const itemInicial = {
  insumoId: '',
  quantidade: '',
  valorUnitario: '',
  dataValidade: '',
  lote: ''
};

export default function CompraInsumos() {
  const navigate = useNavigate();
  const [compras, setCompras] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [safras, setSafras] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [compra, setCompra] = useState(compraInicial);
  const [item, setItem] = useState(itemInicial);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);

  const total = useMemo(
    () => itens.reduce((acc, x) => acc + (Number(x.quantidade) * Number(x.valorUnitario)), 0),
    [itens]
  );

  const carregar = async () => {
    setLoading(true);
    try {
      const [comprasRes, fornecedoresRes, safrasRes, insumosRes] = await Promise.all([
        api.get('/compras-insumos'),
        api.get('/fornecedor'),
        api.get('/safras'),
        api.get('/insumos')
      ]);

      setCompras(Array.isArray(comprasRes.data) ? comprasRes.data : []);
      setFornecedores(Array.isArray(fornecedoresRes.data) ? fornecedoresRes.data : []);
      setSafras(Array.isArray(safrasRes.data) ? safrasRes.data : []);
      setInsumos(Array.isArray(insumosRes.data) ? insumosRes.data : []);
    } catch {
      showError('Erro ao carregar compras de insumos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const adicionarItem = () => {
    if (!item.insumoId || Number(item.quantidade) <= 0 || Number(item.valorUnitario) <= 0) {
      showError('Informe insumo, quantidade e valor unitário.');
      return;
    }

    const insumo = insumos.find(x => String(x.id) === String(item.insumoId));
    setItens(prev => [
      ...prev,
      {
        ...item,
        insumoNome: insumo?.nome || `Insumo ${item.insumoId}`
      }
    ]);
    setItem(itemInicial);
  };

  const removerItem = (index) => {
    setItens(prev => prev.filter((_, i) => i !== index));
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (itens.length === 0) {
      showError('Adicione ao menos um item.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/compras-insumos', {
        fornecedorId: Number(compra.fornecedorId),
        safraId: compra.safraId ? Number(compra.safraId) : null,
        dataCompra: compra.dataCompra,
        dataVencimento: compra.dataVencimento,
        formaPagamento: Number(compra.formaPagamento),
        numeroNota: compra.numeroNota,
        observacao: compra.observacao,
        itens: itens.map(x => ({
          insumoId: Number(x.insumoId),
          quantidade: Number(x.quantidade),
          valorUnitario: Number(x.valorUnitario),
          dataValidade: x.dataValidade || null,
          lote: x.lote || null
        }))
      });

      showSuccess('Compra lançada, estoque atualizado e conta a pagar gerada.');
      setCompra(compraInicial);
      setItens([]);
      await carregar();
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao salvar compra de insumos.');
    } finally {
      setLoading(false);
    }
  };

  const compraColumns = [
    { name: 'Data', selector: row => new Date(row.dataCompra).toLocaleDateString(), sortable: true },
    { name: 'Fornecedor', selector: row => row.fornecedorNome, sortable: true },
    { name: 'Safra', selector: row => row.safraNome || '-', sortable: true },
    { name: 'NF', selector: row => row.numeroNota || '-', sortable: true },
    { name: 'Total', selector: row => Number(row.valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), sortable: true },
    { name: 'Despesa', selector: row => row.despesaId ? `#${row.despesaId}` : '-', sortable: true }
  ];

  const itemColumns = [
    { name: 'Insumo', selector: row => row.insumoNome },
    { name: 'Quantidade', selector: row => Number(row.quantidade).toLocaleString('pt-BR') },
    { name: 'Valor unit.', selector: row => Number(row.valorUnitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
    { name: 'Total', selector: row => (Number(row.quantidade) * Number(row.valorUnitario)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
    { name: 'Lote', selector: row => row.lote || '-' },
    {
      name: 'Ações',
      cell: (_row, index) => (
        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removerItem(index)}>
          <i className="bi bi-trash"></i>
        </button>
      ),
      ignoreRowClick: true
    }
  ];

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3 mb-4">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/menu')}
              disabled={loading}
            >
              <i className="bi bi-arrow-left text-success" />
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0">Compra de Insumos</h5>
              <small className="text-white-50">Entrada de produtos, estoque, custo médio e financeiro</small>
            </div>
          </div>
          <button className="btn btn-light text-success fw-semibold" onClick={carregar} disabled={loading}>
            <i className="bi bi-arrow-clockwise"></i> Atualizar
          </button>
        </div>
        <div className="card-body">
          <form onSubmit={salvar} className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Fornecedor *</label>
              <select className="form-select" value={compra.fornecedorId} onChange={e => setCompra(prev => ({ ...prev, fornecedorId: e.target.value }))} required disabled={loading}>
                <option value="">Selecione...</option>
                {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome} - {f.documento}</option>)}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Safra</label>
              <select className="form-select" value={compra.safraId} onChange={e => setCompra(prev => ({ ...prev, safraId: e.target.value }))} disabled={loading}>
                <option value="">Financeiro geral</option>
                {safras.map(s => <option key={s.id} value={s.id}>{s.cultura}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Compra *</label>
              <input type="date" className="form-control" value={compra.dataCompra} onChange={e => setCompra(prev => ({ ...prev, dataCompra: e.target.value }))} required disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Vencimento *</label>
              <input type="date" className="form-control" value={compra.dataVencimento} onChange={e => setCompra(prev => ({ ...prev, dataVencimento: e.target.value }))} required disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Forma pagamento</label>
              <select className="form-select" value={compra.formaPagamento} onChange={e => setCompra(prev => ({ ...prev, formaPagamento: e.target.value }))} disabled={loading}>
                <option value={0}>Dinheiro</option>
                <option value={1}>Pix</option>
                <option value={2}>Cartão de crédito</option>
                <option value={3}>Cartão de débito</option>
                <option value={4}>Transferência</option>
                <option value={5}>Boleto</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Número NF</label>
              <input className="form-control" value={compra.numeroNota} onChange={e => setCompra(prev => ({ ...prev, numeroNota: e.target.value }))} disabled={loading} />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Observação</label>
              <input className="form-control" value={compra.observacao} onChange={e => setCompra(prev => ({ ...prev, observacao: e.target.value }))} disabled={loading} />
            </div>

            <div className="col-12">
              <hr />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Insumo</label>
              <select className="form-select" value={item.insumoId} onChange={e => setItem(prev => ({ ...prev, insumoId: e.target.value }))} disabled={loading}>
                <option value="">Selecione...</option>
                {insumos.map(i => <option key={i.id} value={i.id}>{i.nome} - saldo {Number(i.estoqueAtual || 0).toLocaleString('pt-BR')} {i.unidadeMedida}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Quantidade</label>
              <input type="number" step="0.0001" className="form-control" value={item.quantidade} onChange={e => setItem(prev => ({ ...prev, quantidade: e.target.value }))} disabled={loading} />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Valor unitário</label>
              <CurrencyInput className="form-control" value={item.valorUnitario} onValueChange={value => setItem(prev => ({ ...prev, valorUnitario: value }))} placeholder="0,00" disabled={loading} />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Validade</label>
              <input type="date" className="form-control" value={item.dataValidade} onChange={e => setItem(prev => ({ ...prev, dataValidade: e.target.value }))} disabled={loading} />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Lote</label>
              <input className="form-control" value={item.lote} onChange={e => setItem(prev => ({ ...prev, lote: e.target.value }))} disabled={loading} />
            </div>

            <div className="col-12 d-flex justify-content-between align-items-center">
              <button type="button" className="btn btn-outline-success" onClick={adicionarItem} disabled={loading}>
                <i className="bi bi-plus-circle"></i> Adicionar item
              </button>
              <strong>Total: {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
            </div>

            <div className="col-12">
              <DataTable columns={itemColumns} data={itens} dense striped noDataComponent={<div className="text-muted">Nenhum item adicionado.</div>} />
            </div>

            <div className="col-12 d-flex justify-content-end">
              <button className="btn btn-success" disabled={loading}>
                <i className="bi bi-save"></i> Lançar compra
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-header bg-light">
          <strong>Compras lançadas</strong>
        </div>
        <div className="card-body">
          <DataTable columns={compraColumns} data={compras} pagination striped highlightOnHover progressPending={loading} />
        </div>
      </div>
    </div>
  );
}
